import { Recipe } from "../../../../api/recipeSimulator";
import { colorMap } from "../../../../utils/utils";
import { BREAKDOWN_SORT_ORDER, BREAKDOWN_TYPE, BREAKDOWN_TYPES } from "../recipeImpactChart.constants";
import { BreakdownItem, ImpactBreakdown } from "../recipeImpactChart.types";
import { buildItemId, getBreakdownList, getItemName } from "./breakdownItem.utill";

/**
 * Collects every unique breakdown item in discovery order (BREAKDOWN_TYPES iteration
 * order, before stack sort). Used for stable palette and pattern index assignment.
 */
export const collectBreakdownItemsInDiscoveryOrder = (
  breakdowns: ImpactBreakdown[]
): BreakdownItem[] => {
  const items: BreakdownItem[] = [];
  const seenIds = new Set<string>();

  breakdowns.forEach(breakdown => {
    BREAKDOWN_TYPES.forEach(type => {
      getBreakdownList(breakdown, type).forEach(entry => {
        const id = buildItemId(entry, type);
        if (!seenIds.has(id)) {
          seenIds.add(id);
          items.push({ id, name: getItemName(entry, type), type });
        }
      });
    });
  });

  return items;
};

/**
 * Collects every unique breakdown item across the provided breakdowns, ordered
 * by stack order (processes at bottom, products in the middle, generic ingredients on top).
 */
export const collectBreakdownItems = (breakdowns: ImpactBreakdown[]): BreakdownItem[] =>
  collectBreakdownItemsInDiscoveryOrder(breakdowns).sort(
    (a, b) => BREAKDOWN_SORT_ORDER[a.type] - BREAKDOWN_SORT_ORDER[b.type]
  );

/** Indexes the raw source items by id so their saved `chartColor` can be read. */
export const indexSourceItemsById = (breakdowns: ImpactBreakdown[]): Record<string, any> => {
  const index: Record<string, any> = {};

  breakdowns.forEach(breakdown => {
    BREAKDOWN_TYPES.forEach(type => {
      getBreakdownList(breakdown, type).forEach(entry => {
        index[buildItemId(entry, type)] = entry;
      });
    });
  });

  return index;
};

/** Indexes chartColor (and related fields) from recipe definition documents. */
export const indexRecipeDefinitionById = (
  originalRecipe?: Recipe["originalRecipe"] | null,
  variants?: Recipe["variants"]
): Record<string, any> => {
  const index: Record<string, any> = {};

  const indexRecipe = (recipe: Recipe["originalRecipe"] | Recipe["variants"][number] | undefined) => {
    if (!recipe) return;
    BREAKDOWN_TYPES.forEach(type => {
      getBreakdownList(recipe as ImpactBreakdown, type).forEach(entry => {
        index[buildItemId(entry, type)] = entry;
      });
    });
  };

  indexRecipe(originalRecipe ?? undefined);
  variants?.forEach(variant => indexRecipe(variant));

  return index;
};

/** Merges multiple source indexes; later entries overlay earlier ones per id. */
export const mergeSourceIndexes = (...indexes: Record<string, any>[]): Record<string, any> => {
  const merged: Record<string, any> = {};

  indexes.forEach(index => {
    Object.entries(index).forEach(([id, entry]) => {
      merged[id] = merged[id] ? { ...merged[id], ...entry } : entry;
    });
  });

  return merged;
};

/** Assigns palette slots by discovery order (first-seen id), independent of stack sort. */
export const assignStablePaletteById = (discoveryOrderItems: BreakdownItem[]): Record<string, string> => {
  const palette: Record<string, string> = {};
  discoveryOrderItems.forEach((item, idx) => {
    palette[item.id] = colorMap[idx + 1];
  });
  return palette;
};

/** Maps each process item id to a stable stripe pattern index (discovery order). */
export const buildPatternIndexById = (discoveryOrderItems: BreakdownItem[]): Record<string, number> => {
  const patternIndexById: Record<string, number> = {};
  let processIndex = 0;

  discoveryOrderItems.forEach(item => {
    if (item.type === BREAKDOWN_TYPE.process) {
      patternIndexById[item.id] = processIndex++;
    }
  });

  return patternIndexById;
};

/**
 * Resolves a color per breakdown item: the saved `chartColor` when present,
 * otherwise a deterministic palette fallback keyed by discovery order.
 */
export const resolveColorById = (
  items: BreakdownItem[],
  sourceIndex: Record<string, any>,
  discoveryOrderItems: BreakdownItem[]
): Record<string, string> => {
  const stablePalette = assignStablePaletteById(discoveryOrderItems);
  const colors: Record<string, string> = {};

  items.forEach(item => {
    const savedColor = sourceIndex[item.id]?.chartColor;
    colors[item.id] = savedColor || stablePalette[item.id];
  });

  return colors;
};